import Button from '../components/ui/Button.jsx';
import { HERO } from '../content/home.js';
import { APP_URL, PRIMARY_CTA } from '../content/site.js';
import WhatsAppIcon from '../components/ui/WhatsAppIcon.jsx';
import heroWebp from '../assets/hero-dashboard.webp';
import heroPng from '../assets/hero-dashboard.png';

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-deep pt-16 pb-28 lg:pt-20 lg:pb-32">
      {/* Warm glow behind the screenshot, matching the design's radial gradient. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_900px_500px_at_80%_0%,rgba(232,163,61,0.14),transparent_60%)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#0d2013] via-deep to-field"
        style={{ zIndex: -1 }}
      />

      {/*
        Phones: headline, then the screenshot, then the subheadline and the
        button, so the product is visible on the first screen and the button
        sits right under the text that explains it.
        Desktop: text on the left, screenshot on the right.

        The text column is `contents` below lg, which lifts its children into
        this grid so `order` can slot the screenshot between them; at lg it is
        an ordinary block again.
      */}
      <div className="wrap relative grid items-center lg:grid-cols-[1.05fr_1fr] lg:gap-12">
        <div className="contents lg:block">
          <p className="order-1 mb-6 inline-flex items-center gap-2 justify-self-start rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[12.5px] font-semibold text-white">
            <span aria-hidden="true">⚡</span>
            {HERO.badge}
          </p>

          <h1 className="order-2 mb-7 font-display text-[32px] font-bold leading-[1.14] text-white sm:text-[40px] lg:mb-5 lg:text-[46px]">
            {HERO.headline} <span className="text-amber">{HERO.headlineAccent}</span>
          </h1>

          <p className="order-4 mb-8 max-w-[480px] text-[16.5px] leading-relaxed text-mist">
            {HERO.sub}
          </p>

          {/* One action, not two. The second button competed with the first
              and sent people sideways into a gallery instead of onward. */}
          <div className="order-5 flex flex-wrap gap-3.5">
            <Button href={PRIMARY_CTA.to} variant="primary">
              {!APP_URL && <WhatsAppIcon size={19} />}
              {APP_URL ? PRIMARY_CTA.label : HERO.cta}
            </Button>
          </div>
        </div>

        <div className="relative order-3 mb-8 lg:order-none lg:mb-0">
          <div
            aria-hidden="true"
            className="absolute inset-0 scale-110 bg-[radial-gradient(circle,rgba(232,163,61,0.18),transparent_65%)] blur-[10px]"
          />
          <picture>
            <source srcSet={heroWebp} type="image/webp" />
            <img
              src={heroPng}
              alt="Chickrah dashboard showing bird population, production rate and profit trend across two farms"
              width="1120"
              height="737"
              loading="eager"
              // Lowercase deliberately: React 18 does not know this attribute
              // and passes unknown props through verbatim, so the camelCase
              // spelling both warns in the console and lands in the DOM as
              // fetchpriority="high" anyway. React 19 adds the camelCase form.
              fetchpriority="high"
              decoding="async"
              className="relative w-full rounded-xl shadow-hero"
            />
          </picture>
        </div>
      </div>
    </section>
  );
}
